import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * ThemeContext - Manages light/dark mode theming and high contrast mode
 */

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  // Initialize from localStorage or system preference
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved;

      // Check system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  // Initialize high contrast mode from localStorage or system preference
  const [highContrast, setHighContrastState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('highContrast');
      if (saved) return saved === 'true';

      // Check system preference for high contrast
      if (window.matchMedia('(prefers-contrast: more)').matches) {
        return true;
      }
    }
    return false;
  });

  // Initialize reduced motion mode from localStorage or system preference - SAT-90
  const [reducedMotion, setReducedMotionState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('reducedMotion');
      if (saved) return saved === 'true';

      // Check system preference for reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return true;
      }
    }
    return false;
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Persist to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply high contrast mode to document
  useEffect(() => {
    const root = document.documentElement;

    if (highContrast) {
      root.dataset.highContrast = 'true';
    } else {
      delete root.dataset.highContrast;
    }

    // Persist to localStorage
    localStorage.setItem('highContrast', String(highContrast));
  }, [highContrast]);

  // Apply reduced motion mode to document - SAT-90
  useEffect(() => {
    const root = document.documentElement;

    if (reducedMotion) {
      root.dataset.reducedMotion = 'true';
    } else {
      // Set to false explicitly to override system preference if user wants motion
      const systemPrefers = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (systemPrefers) {
        root.dataset.reducedMotion = 'false';
      } else {
        delete root.dataset.reducedMotion;
      }
    }

    // Persist to localStorage
    localStorage.setItem('reducedMotion', String(reducedMotion));
  }, [reducedMotion]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      const saved = localStorage.getItem('theme');
      // Only auto-switch if user hasn't set a preference
      if (!saved) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Listen for system preference changes for high contrast
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: more)');

    const handleChange = (e) => {
      const saved = localStorage.getItem('highContrast');
      // Only auto-switch if user hasn't set a preference
      if (!saved) {
        setHighContrastState(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Listen for system preference changes for reduced motion - SAT-90
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (e) => {
      const saved = localStorage.getItem('reducedMotion');
      // Only auto-switch if user hasn't set a preference
      if (!saved) {
        setReducedMotionState(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const toggleHighContrast = () => {
    setHighContrastState(prev => !prev);
  };

  const setHighContrast = (enabled) => {
    setHighContrastState(enabled);
  };

  const toggleReducedMotion = () => {
    setReducedMotionState(prev => !prev);
  };

  const setReducedMotion = (enabled) => {
    setReducedMotionState(enabled);
  };

  const setThemeMode = (mode) => {
    if (['light', 'dark', 'system'].includes(mode)) {
      if (mode === 'system') {
        localStorage.removeItem('theme');
        const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(isDark ? 'dark' : 'light');
      } else {
        setTheme(mode);
      }
    }
  };

  const value = {
    theme,
    isDark: theme === 'dark',
    highContrast,
    reducedMotion,
    toggleTheme,
    toggleHighContrast,
    setHighContrast,
    toggleReducedMotion,
    setReducedMotion,
    setTheme: setThemeMode
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
